<template>
    <div class="upselling" :id="id">
        <div
            v-for="(product, index) in products"
            :key="index"
            ref="upsellingProduct"
            :class="[
                'upselling-product',
                { selected: selectedProduct === index },
                { celebrating: celebratingProduct === index },
                { 'best-offer': product.bestOffer === true },
            ]"
            @click="handleProductClick(index, product.productIds)"
        >
            <span v-if="product.bestOffer === true" class="best-offer-badge">
                {{ product.bestOfferText || "Bestes Angebot" }}
            </span>
            <div class="product-image-wrapper">
                <img :src="product.productImage" alt="Product Image" />
                <div
                    v-if="
                        product.labels &&
                        product.labels != '' &&
                        product.labels.split(';').length !== 0
                    "
                    class="label-wrapper"
                >
                    <div
                        v-for="(label, labelIndex) in product.labels.split(';')"
                        :key="labelIndex"
                        class="label"
                    >
                        {{ label }}
                    </div>
                </div>
            </div>
            <h5>{{ product.headline }}</h5>
            <p>{{ product.text }}</p>
            <div class="items">
                <div class="item">
                    <img :src="product.uspOneIconUrl" />
                    <p>{{ product.uspOneText }}</p>
                </div>
                <div class="item">
                    <img :src="product.uspTwoIconUrl" />
                    <p>{{ product.uspTwoText }}</p>
                </div>
                <div class="item">
                    <img :src="product.uspThreeIconUrl" />
                    <p>{{ product.uspThreeText }}</p>
                </div>
            </div>
            <div class="upselling-price">
                <span :class="{ 'strikethrough': product.savePrice }">{{ product.price }}</span><br />
                <span v-if="product.savePrice" class="save-price">{{ product.savePrice }}</span>
            </div>
            <DTInput
                v-show="product.hasInput && selectedProduct === index"
                ref="upsellingInput"
                :payload="product.inputPayload"
                :placeholder="product.inputPlaceholder"
                :required="true"
                :autoHeight="true"
            />
        </div>
    </div>
</template>

<script>
import DTInput from "./DTInput.vue";
import EventBus from "../event-bus";

export default {
    name: "UpSelling",
    components: {
        DTInput,
    },
    props: {
        id: {
            type: String,
            default: "",
        },
        products: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            selectedProduct: 0,
            celebratingProduct: null,
            celebrationTimeout: null,
        };
    },
    mounted() {
        this.products.forEach((product, index) => {
            if (product.isSelected) {
                this.selectedProduct = index;
                
                if (product.productIds && product.productIds.trim() !== '') {
                    const idsArray = product.productIds.split(',').map(id => id.trim());
                    EventBus.emit("upsellProductSelected", idsArray);
                } else if (product.productId) {
                    EventBus.emit("upsellProductSelected", [product.productId]);
                }
            }
        });
    },
    beforeUnmount() {
        clearTimeout(this.celebrationTimeout);
    },
    methods: {
        handleProductClick(index, productIds) {
            this.$refs.upsellingInput[this.selectedProduct].setRequired(false);

            this.selectedProduct = index;
            this.celebrateSelection(index);

            if (this.products[index].hasInput) {
                this.$refs.upsellingInput[index].setRequired(true);
            }

            if (productIds && productIds.trim() !== '') {
                const idsArray = productIds.split(',').map(id => id.trim());
                EventBus.emit("upsellProductSelected", idsArray);
            } else if (this.products[index].productId) {
                EventBus.emit("upsellProductSelected", [this.products[index].productId]);
            }
        },
        celebrateSelection(index) {
            if (this.prefersReducedMotion()) {
                return;
            }

            clearTimeout(this.celebrationTimeout);
            this.celebratingProduct = index;

            this.celebrationTimeout = setTimeout(() => {
                this.celebratingProduct = null;
                this.celebrationTimeout = null;
            }, 700);
        },
        prefersReducedMotion() {
            return window.matchMedia &&
                window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        },
    },
};
</script>

<style lang="scss" scoped>
@use "@/assets/customizer.vars.scss" as *;
.upselling {
    display: flex;
    flex-direction: column;
    row-gap: 24px;

    .upselling-product {
        position: relative;
        display: grid;
        grid-template-columns: 120px 1fr;
        grid-template-rows: auto;
        align-items: center;
        column-gap: 16px;
        row-gap: 6px;
        width: 100%;
        padding: 10px;
        border: 2px solid $color-light-gray;
        border-radius: 32px;
        background: $color-white;
        transition:
            border-color 0.3s ease,
            transform 0.3s ease;
        cursor: pointer;

        @media (max-width: $breakpoint-mobile) {
            column-gap: 12px;
            row-gap: 6px;
        }

        .best-offer-badge {
            position: absolute;
            top: -13px;
            left: 50%;
            transform: translateX(-50%);
            padding: 3px 10px;
            border-radius: 20px;
            background-color: $color-new-black;
            color: $color-white;
        }

        .product-image-wrapper {
            grid-row: 1 / 4;
            position: relative;

            img {
                width: 100%;
            }

            .label-wrapper {
                position: absolute;
                top: 16px;
                left: -16px;

                .label {
                    padding: 1px 4px;
                    border-radius: 20px;
                    margin-bottom: 4px;
                    background: $color-secondary-dt-orange;
                    font-size: 14px;
                    font-weight: 500;
                    line-height: 14px;
                }
            }
        }

        > h5 {
            grid-column: 2;
            grid-row: 1;
            padding-right: 100px;
            font-size: 20px;
            font-weight: 700;

            @media (max-width: $breakpoint-mobile) {
                padding-right: 0;
                font-size: 16px;
            }
        }

        > p {
            grid-column: 2;
            grid-row: 2;
            padding-right: 100px;
            font-size: 16px;
            font-weight: 700;

            @media (max-width: $breakpoint-mobile) {
                padding-right: 0;
                font-size: 12px;
            }
        }

        .items {
            grid-column: 2;
            grid-row: 3;
            display: flex;
            flex-direction: row;
            align-items: center;
            column-gap: 8px;

            @media (max-width: $breakpoint-tablet) {
                display: none;
            }

            .item {
                display: flex;
                flex-direction: row;
                align-items: center;
                column-gap: 4px;
            }
        }

        .upselling-price {
            position: absolute;
            top: 30px;
            right: 24px;
            text-align: right;
            font-size: 20px;
            font-weight: 700;

            @media (max-width: $breakpoint-mobile) {
                grid-column: 2;
                grid-row: 3;
                position: unset;
                text-align: left;
                font-size: 16px;

                br {
                    display: none;
                }
            }

            .strikethrough {
                text-decoration: line-through;
                font-size: 16px;
                font-weight: 400;

                @media (max-width: $breakpoint-mobile) {
                    font-size: 14px;
                }
            }

            .save-price {
                font-size: 20px;
                font-weight: 700;
                color: #11bd90;

                @media (max-width: $breakpoint-mobile) {
                    display: inline-block;
                    margin-left: 4px;
                    font-size: 16px;
                }
            }
        }

        .dt-input-wrapper {
            grid-column: 1 / 3;
            grid-row: 4;
        }

        &.selected {
            border-color: $color-new-black;

            .label {
                background: $color-new-black !important;
                color: $color-white;
            }
        }

        &.celebrating {
            animation: upselling-card-celebrate 0.7s cubic-bezier(0.22, 1, 0.36, 1);
        }
    }
}

@keyframes upselling-card-celebrate {
    0% {
        transform: scale(1);
    }

    35% {
        transform: scale(1.018);
    }

    70% {
        transform: scale(0.996);
    }

    100% {
        transform: scale(1);
    }
}

@media (prefers-reduced-motion: reduce) {
    .upselling {
        .upselling-product {
            animation: none;
            transition: border-color 0.3s ease;
        }
    }
}
</style>
